'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '@/app/landing-page/components/LandingFooter';
import { ArrowRight, Copy, Check } from 'lucide-react';

function CopyPromptBox({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = typeof children === 'string' ? children : (children as React.ReactElement)?.props?.children ?? '';
    const plainText = Array.isArray(children)
      ? (children as React.ReactNode[]).map((c) => (typeof c === 'string' ? c : '')).join('')
      : typeof children === 'string'
      ? children
      : '';
    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="relative rounded-xl p-6 my-6 border-l-4"
      style={{ backgroundColor: 'rgba(212,137,26,0.05)', borderLeftColor: '#D4891A', border: '1px solid rgba(212,137,26,0.25)', borderLeft: '4px solid #D4891A' }}
    >
      <div className="font-body text-sm pr-10" style={{ color: '#2d3748', lineHeight: '1.85' }}>
        {children}
      </div>
      <button
        onClick={handleCopy}
        className="absolute top-4 right-4 flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150"
        style={{ backgroundColor: copied ? 'rgba(212,137,26,0.15)' : 'rgba(212,137,26,0.1)', color: '#D4891A' }}
        aria-label="Copy prompt"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function AiForEmailsPage() {
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How AI Can Help With Emails",
    "url": "https://thatclicked.com/learn/ai-for-emails",
    "author": {
      "@type": "Person",
      "name": "Anna Rippon",
      "url": "https://thatclicked.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "That Clicked",
      "url": "https://thatclicked.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://thatclicked.com/learn/ai-for-emails"
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SiteNav currentPath="/learn/ai-for-emails" />

      <main className="pt-24 pb-24 lg:pt-36 lg:pb-36">
        <div className="max-w-2xl mx-auto px-6 lg:px-0">

          <div className="flex items-center gap-2 mb-6 mt-8">
            <div className="h-px w-8 flex-shrink-0" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-widest" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>
              By Anna Rippon, ThatClicked
            </span>
          </div>

          <h1
            className="font-display font-semibold mb-8"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: '#1B2A4A', lineHeight: '1.18', letterSpacing: '-0.01em' }}
          >
            How AI Can Help With Emails
          </h1>

          <div className="h-px w-full mb-10" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="space-y-6 font-body" style={{ color: '#2d3748', lineHeight: '1.85', fontSize: '1.05rem' }}>

            <h2 className="font-display font-semibold pt-2" style={{ fontSize: '1.15rem', color: '#D4891A', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.85rem' }}>
              THE SITUATION
            </h2>

            <p>
              It is 8.47am. You have 34 unread emails.
            </p>

            <p>
              One of them is a thread that has been going back and forth between six people for three days and contains what you suspect is an important decision, buried somewhere in the middle. You have a meeting at 9.
            </p>

            <p>
              Another is a message you have been putting off replying to because you are not quite sure how to say what needs to be said without causing a problem.
            </p>

            <p>
              A third needs a response you could write in your sleep, but it still needs writing.
            </p>

            <p>
              If this sounds familiar, you are in very good company. Email is where most professional time goes and where most professional frustration lives.
            </p>

            <p>
              AI does not solve email. But it helps with it considerably. Here is how.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI can actually do with email
            </h2>

            <p>
              There are three genuinely useful things AI can do with your inbox.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Summarise long threads
            </h3>

            <p>
              If you have a thread with multiple contributors spanning several days, AI can read the whole thing and tell you, in plain English, what was discussed, what was decided, and what you need to do.
            </p>

            <p>
              This takes AI about four seconds. It would take you ten minutes and you would still wonder if you had missed something.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Draft responses to difficult emails
            </h3>

            <p>
              The emails most of us avoid are the ones where the tone matters as much as the content. A message that needs to be firm but not aggressive. Honest but not damaging. Apologetic without being obsequious.
            </p>

            <p>
              AI is genuinely useful here. Give it the context — who the email is from, what the situation is, what you want to achieve, what you want to avoid — and it will produce a draft you can work from.
            </p>

            <p>
              It will not be perfect. It rarely is on the first attempt. But it gives you something to react to instead of a blank page, which is where most of the time goes.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Produce first drafts of routine emails
            </h3>

            <p>
              The emails that are not difficult — the weekly update, the meeting follow-up, the project status — can often be drafted by AI faster than you can open a new message. You give it the key points, it writes the email, you check and send.
            </p>

            <p>
              This is not laziness. It is sensible use of a capable tool for work that does not require your full attention.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI cannot do with email
            </h2>

            <p>
              AI does not know your relationships. It does not know the history between you and the person you are writing to. It does not know the organisational politics, the unspoken context, or the thing that happened in the meeting three months ago that is relevant to this message.
            </p>

            <p>
              You do. Which means you are still in charge of the judgment call. AI drafts. You decide.
            </p>

            <p>
              Also: AI occasionally produces emails that sound slightly too formal, or slightly too warm, or slightly off-register for your voice. This is fixable — ask it to adjust. But it means you need to read what comes back rather than sending it unchecked.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What good results look like
            </h2>

            <p>
              Here is the kind of prompt that gets useful results for summarising a thread:
            </p>

            <CopyPromptBox>
              Summarise this email thread and list any decisions that were made and any actions I need to take, including who owns them.
            </CopyPromptBox>

            <p>
              Paste that into Copilot inside an open email thread, or into ChatGPT with the thread content below it. What comes back will be a clean summary with decisions at the top and actions below.
            </p>

            <p>
              For a difficult email:
            </p>

            <CopyPromptBox>
              Help me write a reply to this email. The situation is: [describe what happened and what you need to say]. The tone should be professional but warm. I want to [achieve this outcome] without [causing this problem]. Keep it under 200 words.
            </CopyPromptBox>

            <p>
              Read what comes back. Adjust anything that does not sound like you. Send the version that does.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              Honest expectations
            </h2>

            <p>
              AI will save you time on email. It will not transform your inbox overnight.
            </p>

            <p>
              The biggest gains come from two things: summarising long threads (immediate and significant) and drafting difficult messages (meaningful, once you get used to giving it enough context).
            </p>

            <p>
              The routine email drafting is genuinely useful but takes a little practice to prompt well. Most people find the first few attempts produce something too generic. The fix is always the same: give it more context, and iterate once.
            </p>

          </div>

          {/* Where to go deeper */}
          <div className="mt-14 rounded-xl p-8 border" style={{ backgroundColor: 'white', borderColor: 'rgba(27,42,74,0.12)' }}>
            <h2 className="font-display font-semibold mb-5" style={{ fontSize: '1.15rem', color: '#1B2A4A' }}>
              Where to go deeper
            </h2>
            <p className="font-body text-sm mb-5" style={{ color: '#2d3748', lineHeight: '1.75' }}>
              This is covered in detail in Module 1 (Copilot at Work), specifically the Copilot in Outlook lesson. If you have Copilot through work, that lesson walks you through exactly how to use it on a real email thread. If you use ChatGPT instead, the prompts work identically — just paste the email content in alongside them.
            </p>
            <ul className="space-y-2">
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 1: Copilot at Work <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/learn/ai-for-meeting-preparation" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Related: How AI Can Help You Prepare For Meetings
                </Link>
              </li>
            </ul>
          </div>

          <div className="h-px w-full mt-14 mb-12" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="text-center">
            <p className="font-body text-base mb-6" style={{ color: 'rgba(27,42,74,0.7)' }}>That Clicked is a plain-English AI training platform for professionals over 50.</p>
            <Link
              href="/free-lesson-screen"
              className="group inline-flex items-center justify-center gap-2.5 font-body font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 active:scale-95"
              style={{ backgroundColor: '#D4891A', color: 'white' }}
            >
              Learn more AI skills — first two lessons free
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>

        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
